import {
  compose,
  insert,
  isEmpty,
  length,
  prop,
  values,
  sortBy,
  propOr,
} from 'ramda'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { useSelector } from 'hooks/redux'
import { insertIf } from 'utils/types'
import { getDashComponents, getVehicleSubType } from 'selectors/dash'
import { getModifiedLocationId } from 'components/SwitchAndGauge/utils'
import DropLocation from 'components/DragAndDrop/DropLocation/DropLocation'
import DraggablePart from 'components/SwitchAndGauge/DashDrawer/DraggableList/DraggablePart/DraggablePart'
import {
  CollapsibleList,
  SectionHeader,
} from 'components/SwitchAndGauge/DashDrawer/DraggableList/DashPanelSectionElements'
import {
  LP018,
  MSF_POCKET_LOCATIONS_MAP,
  POCKET_LABELS,
} from 'components/SwitchAndGauge/definitions/dashConstants'
import strings from 'utils/strings'
import { Paragraph } from 'components/patterns'
import { StaticPart, PocketSection } from './DashPanelSection.style'

const insertStaticPart = (staticPart, locations) => {
  if (!staticPart) return locations

  const { afterIndex } = staticPart

  return insert(afterIndex, staticPart, locations)
}

const shouldAddStaticPart = ({ afterIndex }, index) => afterIndex === index

const DashPanelSection = ({ definition }) => {
  const getGroups = compose(sortBy(prop('order')), values, prop('groups'))
  const components = useSelector(getDashComponents)
  const groupDefinitions = getGroups(definition)
  const renderComponentCollection = (groupDefinition) => (
    <ComponentCollection
      {...groupDefinition}
      components={components}
      key={`switch-collection-${groupDefinition.id}`}
    />
  )

  const sections = groupDefinitions.map((group) => {
    // handle pockets
    const { id: groupId } = group
    const pocketLocation = MSF_POCKET_LOCATIONS_MAP[groupId]
    const pocketComponent = components[pocketLocation]

    if (pocketComponent) {
      const label = POCKET_LABELS[pocketLocation]
      return (
        <PocketSection key={groupId}>
          <Paragraph key={groupId} bold style={{ fontSize: 12 }}>
            {label}
          </Paragraph>
        </PocketSection>
      )
    }

    return renderComponentCollection(group)
  })

  const { id: definitionId } = definition
  return (
    <div key={definitionId} className="dash-section-panel-container">
      <SectionHeader definition={definition} className="dash-panel-header" />
      <div className="dash-panel-switches">{sections}</div>
    </div>
  )
}

DashPanelSection.propTypes = {
  definition: PropTypes.object,
}

const ComponentCollection = React.memo(
  ({
    id,
    label,
    locations,
    staticPart,
    onlyShowIfPartExist,
    canDragGroup,
    components,
  }) => {
    const [showDetails, setShowDetails] = useState(false)

    const vehicleSubType = useSelector(getVehicleSubType)
    const allLocations = insertStaticPart(staticPart, locations)

    const handleViewDetailClick = (val) => {
      setShowDetails(val)
    }

    const slots = allLocations.reduce((acc, location, index) => {
      if (shouldAddStaticPart(location, index)) {
        const { label: staticPartLabel } = location
        return [
          ...acc,
          <StaticPart key={staticPartLabel}>{staticPartLabel}</StaticPart>,
        ]
      }

      const component = components[location]
      const partExists = Boolean(component)

      if (onlyShowIfPartExist && !partExists) return acc

      // PBI 55837
      const showLp018 =
        getModifiedLocationId(
          location,
          propOr(null, 'panelNumber', component),
          vehicleSubType
        ) === strings.lp017_and_lp018

      return [
        ...acc,
        <div
          className={location}
          key={location}
          data-testid="collapsible-list-item"
        >
          <DropLocation location={location}>
            <DraggablePart location={location} viewDetails={showDetails} />
          </DropLocation>
        </div>,
        insertIf(
          showLp018,
          <div className={LP018} key={LP018}>
            <DropLocation location={LP018}>
              <DraggablePart
                location={location}
                viewDetails={showDetails}
                displayLocation={LP018}
              />
            </DropLocation>
          </div>
        ),
      ]
    }, [])

    if (isEmpty(slots)) return null

    const showCollapsibleTitle = Boolean(label && length(slots))

    return (
      <CollapsibleList
        key={`${id}-${label}-list`}
        id={id}
        label={label}
        slots={slots}
        showCollapsibleTitle={showCollapsibleTitle}
        canDragGroup={canDragGroup}
        viewDetail={showDetails}
        onClickViewDetail={handleViewDetailClick}
      />
    )
  }
)

ComponentCollection.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  locations: PropTypes.array,
  staticPart: PropTypes.any,
  onlyShowIfPartExist: PropTypes.bool,
  canDragGroup: PropTypes.bool,
  components: PropTypes.any,
}

export default DashPanelSection
